import { NetworkStatus, useLazyQuery, useReactiveVar } from '@apollo/client';
import {
  AnimationClassNames,
  ContextualMenu,
  DirectionalHint,
  IColumn,
  Icon,
  IDetailsRowBaseProps,
  IDetailsRowProps,
  IModalStyles,
  IRenderFunction,
  Modal,
  SelectionMode,
  Stack,
  Text,
  TooltipHost,
  useTheme,
} from '@fluentui/react';
import { ApprovalHistoryItem } from 'approvals/ApprovalsAllUsers';
import {
  ApprovalHistorySearch,
  ApprovalHistorySearchVariables,
} from 'approvals/ApprovalsAllUsers/__generated__/ApprovalHistorySearch';
import {
  ApprovalHistorySearchFilterTotals,
  ApprovalHistorySearchFilterTotalsVariables,
} from 'approvals/ApprovalsAllUsers/__generated__/ApprovalHistorySearchFilterTotals';
import {
  ApprovalHistorySearchHints,
  ApprovalHistorySearchHintsVariables,
} from 'approvals/ApprovalsAllUsers/__generated__/ApprovalHistorySearchHints';
import { branchUserList_branchUserList_nodes } from 'approvals/ApprovalsAllUsers/__generated__/BranchUserList';
import { useColumns } from 'approvals/ApprovalsAllUsers/column.data';
import {
  getFilterParams,
  toApprovalHistoryFilterVariables,
  toOrderByVariable,
} from 'approvals/ApprovalsAllUsers/utils';
import { ApprovalSearchBar } from 'approvals/ApprovalSearchBar';
import clsx from 'clsx';
import { AmountColumnTextView } from 'common/components/AmountView/AmountColumnTextView';
import { AmountTextView } from 'common/components/AmountView/AmountTextView';
import { CloseButton } from 'common/components/Buttons';
import { FilterArrayType } from 'common/components/Filters';
import {
  HighLightActiveLink,
  HighlightTextView,
} from 'common/components/HighLight';
import { InfiniteList } from 'common/components/InfiniteList';
import {
  MultiSelectOption,
  MultiSelectTags,
} from 'common/components/MultiSelectTags';
import { ColumnData } from 'common/components/SearchBar';
import { StatusIcon } from 'common/components/StatusIcon';
import { TABLE_ROWS } from 'common/constants';
import { useCommonStyles } from 'common/styles';
import {
  ApprovalHistoryFilter,
  SearchRequestInput,
} from 'common/types/globalTypes';
import { getSortedColumns } from 'common/utils/columnUtilities';
import { OrderDirection, SortOrder } from 'common/utils/commonTypes';
import {
  dateConvertions,
  dateFormat,
  getGlobalDateFormat,
} from 'common/utils/dateFormats';
import { useEffect, useState } from 'react';
import { globalMode, setGlobalSearchText } from 'utility/cache/ui';
import TRANSACTION_APPROVAL_HISTORY_SEARCH from '../../../../../approvals/ApprovalsAllUsers/ApprovalHistorySearch.graphql';
import TRANSACTION_APPROVAL_HISTORY_SEARCH_TOTALS from '../../../../../approvals/ApprovalsAllUsers/ApprovalHistorySearchFilterTotals.graphql';
import APPROVAL_HISTORY_SEARCH_HINTS from '../../../../../approvals/ApprovalsAllUsers/ApprovalHistorySearchHints.graphql';
import { useStyles } from './index.styles';
import { ViewTransactionApprovalDetails } from './ViewTransactionApprovalDetails';

interface TransactionsApprovalModalProps {
  filterApprovalId: string;
  onDismiss: () => void;
  isModalOpen: boolean;
}
const customModalStyles: Partial<IModalStyles> = {
  scrollableContent: {
    overflowY: 'unset',
    maxHeight: 'none',
    padding: 0,
    display: 'contents',
  },
};

export const TransactionsApprovalModal: React.FC<
  TransactionsApprovalModalProps
> = ({ filterApprovalId, onDismiss, isModalOpen }) => {
  const styles = useStyles();
  const theme = useTheme();
  const globalState = useReactiveVar(globalMode);

  const commonStyles = useCommonStyles();
  let approvalList: ApprovalHistoryItem[] = [];

  const [
    fetchApprovalHistory,
    {
      loading: approvalHistoryListLoading,
      data: approvalHistoryListData,
      networkStatus,
      fetchMore,
    },
  ] = useLazyQuery<ApprovalHistorySearch, ApprovalHistorySearchVariables>(
    TRANSACTION_APPROVAL_HISTORY_SEARCH,
    {
      variables: {
        first: TABLE_ROWS,
        isHistorySearch: false,
        approverUserId: null,
        isApproverUserApprover: false,
        isApproverUserRequester: false,
        isQueuedSearch: false,
        isLinkSearch: true,
        searchRequest: { searchText: [''], searchHintId: null },
        orderBy: [
          'DESCRIPTION_1_ASC',
          'SORT_ORDER_GROUP_DESC',
          'PRIMARY_KEY_ASC',
        ],
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-only',
    }
  );
  const [fetchTotals, { data: totalsData, variables: totalsVariable }] =
    useLazyQuery<
      ApprovalHistorySearchFilterTotals,
      ApprovalHistorySearchFilterTotalsVariables
    >(TRANSACTION_APPROVAL_HISTORY_SEARCH_TOTALS, {
      variables: {
        isQueuedSearch: false,
        isHistorySearch: false,
        approverUserId: null,
        isApproverUserApprover: false,
        isApproverUserRequester: false,
        searchRequest: {
          searchText: [''],
          searchHintId: null,
          isMatchAny: true,
        },
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-only',
    });

  const [fetchApprovalHistoryHints, { data: approvalHistoryHintsData }] =
    useLazyQuery<
      ApprovalHistorySearchHints,
      ApprovalHistorySearchHintsVariables
    >(APPROVAL_HISTORY_SEARCH_HINTS, {
      variables: {
        searchRequest: { searchText: [''], searchHintId: null },
        isHistorySearch: false,
        approverUserId: null,
        isApproverUserApprover: false,
        isApproverUserRequester: false,
        isQueuedSearch: false,
        isLinkSearch: true,
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-only',
    });

  const { columns } = useColumns(true);

  const [gridColumns, setGridColumns] = useState<ColumnData[]>(columns);
  const [selectedHint, setselectedHint] = useState<number | null>();
  const [searchFilters, setSearchFilters] = useState<FilterArrayType[]>();
  const [queuedFilter, setqueuedFilter] = useState(false);
  const [historyFilter, sethistoryFilter] = useState(false);
  const [actionPendingFilter, setActionPendingFilter] = useState(true);
  const [sortOrderParam, setSortOrderParam] = useState<SortOrder>();
  const [isUserApprover, setIsUserApprover] = useState<boolean>(false);
  const [isUserRequestor, setIsUserRequestor] = useState<boolean>(false);
  const [approverUser, setApproverUser] =
    useState<branchUserList_branchUserList_nodes | null>(null);
  const [unSignedCheck, setUnSignedCheck] = useState(false);
  const [searchValue, setSearchValue] = useState<string | undefined | null>(
    null
  );
  const [selectedItem, setSelectedItem] = useState<string | undefined>(
    undefined
  );
  const [listMode, setListMode] = useState(true);

  const globalSearchText = useReactiveVar(setGlobalSearchText);
  // Animations
  const SlideLeftOut = AnimationClassNames.slideLeftOut400;
  const slideRightIn = AnimationClassNames.slideRightIn400;

  const searchHintsOptions: MultiSelectOption[] =
    approvalHistoryHintsData?.approvalHistorySearchHints?.nodes.map(
      (hint) =>
        ({
          key: hint.id?.toString(),
          text: `${hint.caption} (${hint.resultsCount})`,
        } as MultiSelectOption)
    ) || [];
  const refetching =
    networkStatus === NetworkStatus.refetch ||
    networkStatus === NetworkStatus.setVariables;

  const transformedData =
    approvalHistoryListData?.approvalHistorySearch?.nodes.map(
      (appr) =>
        ({
          ...appr,
          ...appr.approvalHistoryApprover,
          key: appr.id,
          name: appr.description1,
          number: appr.description3,
          description: appr.description2,
          date: appr.date1 ? appr.date1 : '',
          amount: appr.amount1?.toString(),
          currencyData: appr.currency?.isoCode,
          role: appr.approvalPersona,
          status: appr.iconType?.iconType,
          departmentName: appr.department?.name,
          businessUnitName: appr.businessUnit?.name,
          periodYear: appr.companyCorporatePeriod?._periodYear,
          additionalApprovalInformation: appr.additionalApprovalInformation,
          phaseName: appr.approvalHistoryPhaseType?.phaseName,
        } as ApprovalHistoryItem)
    );
  if (transformedData) approvalList = transformedData;

  const _renderItemColumn = (
    item: ApprovalHistoryItem | undefined,
    _index: number | undefined,
    column: IColumn | undefined
  ) => {
    if (item) {
      const fieldContent = item?.[
        column?.fieldName as keyof ApprovalHistoryItem
      ] as string;
      let approverData;
      if (item.approvalHistoryApprover?.approvers)
        approverData = item.approvalHistoryApprover?.approvers
          .split(',')
          .join(', ');
      let rejectorsList: string = '';
      if (item?.approvalHistoryApprover?.rejectors)
        rejectorsList = item?.approvalHistoryApprover?.rejectors
          .split(',')
          .join(', ');

      const { _isUrgentApproval } = { ...item };
      const Content: React.FC = () => {
        return (
          <Stack className={styles.container}>
            <Stack className={styles.urgentHeader}>
              <Text className={styles.title}>Urgent</Text>
            </Stack>
            <Stack tokens={{ padding: 20 }}>
              <Text>{item?.approvalTopic}</Text>
            </Stack>
          </Stack>
        );
      };
      switch (column?.key) {
        case 'urgencyLevel':
          return (
            <TooltipHost className={styles.containerMain} content={<Content />}>
              <Stack
                tokens={{ padding: '0px 0px 0px 10px' }}
                verticalAlign="center"
              >
                {_isUrgentApproval && (
                  <Icon
                    iconName="FabricFolderFill"
                    className={commonStyles.urgentBox}
                  />
                )}
              </Stack>
            </TooltipHost>
          );
        case 'name':
          return (
            <Stack
              tokens={{ childrenGap: 10 }}
              verticalAlign="center"
              horizontal
              className={styles.onrenderColumnStack}
            >
              <HighLightActiveLink
                key={item?.key}
                highlightText={globalSearchText}
                text={item?.name}
                to={`/approval_history/approval/${item?.key}`}
                preventDefault
                onLinkClick={() => {
                  setSelectedItem(item.id);
                  setListMode(false);
                }}
              />
            </Stack>
          );
        case 'status':
          return (
            <StatusIcon
              approval
              iconType={item?.iconType!}
              approvalData={{
                id: item?.id!,
                _pendingApprovers: item?._pendingApprovers,
              }}
            />
          );
        case 'date':
          return (
            <Stack
              verticalAlign="center"
              className={styles.onrenderColumnStack}
            >
              <HighlightTextView
                className={styles.contentColumnAlignRight}
                highlightText={globalSearchText}
                text={
                  fieldContent === null || !fieldContent
                    ? ''
                    : dateFormat(dateConvertions(fieldContent))
                }
              />
            </Stack>
          );
        case 'amount':
          return (
            <AmountColumnTextView
              value={fieldContent}
              searchText={globalSearchText}
            />
          );
        case 'requesterDate':
          return (
            <Stack
              verticalAlign="center"
              className={styles.onrenderColumnStack}
            >
              <HighlightTextView
                highlightText={globalSearchText}
                text={
                  item?.approvalHistoryApprover?.requesterDate! &&
                  getGlobalDateFormat(
                    item.approvalHistoryApprover?.requesterDate!
                  )
                }
              />
            </Stack>
          );
        case 'approvedDate':
          return (
            <Stack
              verticalAlign="center"
              className={styles.onrenderColumnStack}
            >
              <HighlightTextView
                highlightText={globalSearchText}
                text={
                  item?.approvalHistoryApprover?.approvedDate! &&
                  getGlobalDateFormat(
                    item.approvalHistoryApprover?.approvedDate!
                  )
                }
              />
            </Stack>
          );
        case 'rejectedDate':
          return (
            <Stack
              verticalAlign="center"
              className={styles.onrenderColumnStack}
            >
              <HighlightTextView
                highlightText={globalSearchText}
                text={
                  item?.approvalHistoryApprover?.rejectedDate! &&
                  getGlobalDateFormat(
                    item.approvalHistoryApprover?.rejectedDate!
                  )
                }
              />
            </Stack>
          );
        case 'amendedDate':
          return (
            <Stack
              verticalAlign="center"
              className={styles.onrenderColumnStack}
            >
              <HighlightTextView
                highlightText={globalSearchText}
                text={
                  item?.approvalHistoryApprover?.amendedDate! &&
                  getGlobalDateFormat(
                    item.approvalHistoryApprover?.amendedDate!
                  )
                }
              />
            </Stack>
          );
        case 'approvers':
          return (
            <Stack
              verticalAlign="center"
              className={styles.onrenderColumnStack}
            >
              <HighlightTextView
                highlightText={globalSearchText}
                text={approverData ? approverData : ''}
              />
            </Stack>
          );
        case 'rejectors':
          return (
            <Stack
              verticalAlign="center"
              className={styles.onrenderColumnStack}
            >
              <HighlightTextView
                highlightText={globalSearchText}
                text={rejectorsList}
              />
            </Stack>
          );
        default:
          return (
            <Stack
              verticalAlign="center"
              className={styles.onrenderColumnStack}
            >
              {fieldContent && (
                <HighlightTextView
                  highlightText={globalSearchText}
                  text={fieldContent}
                />
              )}
            </Stack>
          );
      }
    }
  };
  const _renderDetailsFooterItemColumn: IDetailsRowBaseProps['onRenderItemColumn'] =
    (_item, _index, column) => {
      if (column?.key === 'amount') {
        return (
          <AmountTextView
            variant="medium"
            className={clsx(styles.amountStack, styles.contentColumnAlignRight)}
            value={totalsData?.approvalHistorySearchFilterTotals?.totalAmount1!}
          />
        );
      }

      return '';
    };

  const _onRenderRow: IRenderFunction<IDetailsRowProps> = (
    props,
    defaultRender
  ) => {
    if (!props) {
      return null;
    }

    const item: ApprovalHistoryItem = { ...props.item };
    const { _isUrgentApproval } = { ...item };

    const newProps: IDetailsRowProps | undefined = props
      ? {
          ...props,
          className: clsx(
            styles.rowBaseStyle,
            _isUrgentApproval ? commonStyles.urgentRow : styles.rowBaseStyle
          ),
        }
      : undefined;
    const tootTipMessage = 'View to approve.';
    const tooltipVisible =
      !item.isRequester &&
      !(
        item.iconType?.isApprovalActionAvailable &&
        item._isUserEnMasseApprovalAllowed &&
        !item._isValidating
      );
    const toolTipContent = tooltipVisible ? tootTipMessage : '';

    return (
      <>
        <TooltipHost
          content={toolTipContent}
          directionalHint={DirectionalHint.topLeftEdge}
          tooltipProps={{
            styles: { root: { marginLeft: 100 } },
          }}
        >
          {defaultRender?.(newProps)}
        </TooltipHost>
      </>
    );
  };

  const handleSearch = (
    showMore: boolean,
    newHints: boolean,
    globalValue: boolean
  ) => {
    const searchRequest: SearchRequestInput = {
      searchText: [searchValue ? searchValue : ''],
      searchHintId: selectedHint,
    };
    const searchHintsRequest: SearchRequestInput = {
      searchText: [searchValue ? searchValue : ''],
      searchHintId: null,
    };

    const variables: ApprovalHistorySearchVariables = {
      first: TABLE_ROWS,
      isHistorySearch: historyFilter,
      approverUserId: approverUser?.id,
      filterApprovalId: filterApprovalId,
      isApproverUserApprover: isUserApprover,
      isApproverUserRequester: isUserRequestor,
      isOnlyUnsignedApprovals: unSignedCheck,
      isQueuedSearch: queuedFilter,
      searchRequest,
      after: showMore
        ? approvalHistoryListData?.approvalHistorySearch?.pageInfo.endCursor
        : undefined,
      filter: searchFilters?.length
        ? ({
            and: [...toApprovalHistoryFilterVariables(searchFilters)],
          } as ApprovalHistoryFilter)
        : undefined,
      orderBy: toOrderByVariable(sortOrderParam!, historyFilter),
      isLinkSearch: globalValue,
    };
    const filterParams = getFilterParams(searchFilters || []);

    const variableHints: ApprovalHistorySearchHintsVariables = {
      searchRequest: searchHintsRequest,
      filterApprovalId: filterApprovalId,
      isHistorySearch: historyFilter,
      approverUserId: approverUser?.id,
      isApproverUserApprover: isUserApprover,
      isApproverUserRequester: isUserRequestor,
      isOnlyUnsignedApprovals: unSignedCheck,
      isQueuedSearch: queuedFilter,
      isLinkSearch: globalValue,
    };
    const totalFilterVariables: ApprovalHistorySearchFilterTotalsVariables = {
      isLinkSearch: globalValue,
      isQueuedSearch: queuedFilter,
      searchRequest,
      isHistorySearch: historyFilter,
      approverUserId: approverUser?.id,
      isApproverUserApprover: isUserApprover,
      isApproverUserRequester: isUserRequestor,
      isOnlyUnsignedApprovals: unSignedCheck,
      filterApprovalId: filterApprovalId,
      ...filterParams,
    };

    if (showMore) fetchMore?.({ variables });
    else {
      fetchApprovalHistory({ variables });
      fetchTotals({ variables: totalFilterVariables });
    }
    if (newHints) fetchApprovalHistoryHints({ variables: variableHints });
  };
  const showFooter =
    !refetching &&
    parseFloat(totalsData?.approvalHistorySearchFilterTotals?.totalAmount1!) >
      0;
  const _onColumnClick = (clickedColumn: ColumnData) => {
    const { newColumns, desc } = getSortedColumns(clickedColumn, gridColumns);
    setGridColumns(newColumns);
    setSortOrderParam({
      column: clickedColumn.key,
      direction: desc ? OrderDirection.DESC : OrderDirection.ASC,
    });
  };

  useEffect(() => {
    handleSearch(false, true, globalState);
  }, [
    searchValue,
    queuedFilter,
    historyFilter,
    searchFilters,
    actionPendingFilter,
    isUserApprover,
    isUserRequestor,
    unSignedCheck,
    approverUser,
    selectedHint,
    globalState,
    sortOrderParam,
  ]);

  return (
    <Modal
      isOpen={isModalOpen}
      onDismiss={onDismiss}
      styles={customModalStyles}
      containerClassName={styles.modalContainer}
      dragOptions={{
        moveMenuItemText: 'Move',
        closeMenuItemText: 'Close',
        menu: ContextualMenu,
        dragHandleSelector: '.ms-Modal-scrollableContent > div:first-child',
      }}
    >
      <Stack
        horizontal
        style={{
          backgroundColor: theme.palette.neutralLighterAlt,
          position: 'sticky',
          top: 0,
        }}
        tokens={{ padding: '20px ' }}
      >
        {listMode ? (
          <Text variant="xLarge">All Approvals</Text>
        ) : (
          <Stack
            horizontal
            verticalAlign="center"
            onClick={() => setListMode(true)}
            tokens={{ childrenGap: 10 }}
            style={{ cursor: 'pointer' }}
          >
            <Icon style={{ paddingTop: 5 }} iconName="ChromeBack" />

            <Text variant="xLarge">Go Back</Text>
          </Stack>
        )}
        <Stack style={{ marginLeft: 'auto' }}>
          <CloseButton onClick={onDismiss} />
        </Stack>
      </Stack>
      {listMode && (
        <Stack
          style={{
            height: 1120,
            width: 1500,
          }}
          className={!listMode ? SlideLeftOut : slideRightIn}
          tokens={{ padding: '0px', childrenGap: 30 }}
        >
          <Stack tokens={{ padding: '20px' }}>
            {searchHintsOptions.length > 0 && (
              <MultiSelectTags
                multiSelect={false}
                options={searchHintsOptions}
                onSelectionChange={(selctedTags) => {
                  const hints = selctedTags.map((item) => parseInt(item.key));
                  setselectedHint(hints[0]);
                }}
              />
            )}
          </Stack>
          <ApprovalSearchBar
            isAllApproval
            setIsUserApprover={setIsUserApprover}
            isUserApprover={isUserApprover}
            isUserRequestor={isUserRequestor}
            setIsUserRequestor={setIsUserRequestor}
            setApproverUser={setApproverUser}
            approverUser={approverUser}
            columns={gridColumns}
            hintsAvailable={false}
            onQueuedToogle={(value) => {
              setqueuedFilter(value);
            }}
            onFilterChange={setSearchFilters}
            onHistoryToogle={(value) => {
              sethistoryFilter(value);
            }}
            onActionPendingToggle={(value) => {
              setActionPendingFilter(value);
            }}
            actionPendingFilter={actionPendingFilter}
            onRefresh={() => handleSearch(false, false, false)}
            onEnterPress={(value) => {
              setSearchValue(value);
              setGlobalSearchText(value);
            }}
            onToggleVisibility={(columns) => {
              setGridColumns(columns);
            }}
            onGlobalToggle={(value) => {
              handleSearch(false, true, value);
            }}
            onUnsignedCheck={setUnSignedCheck}
          />
          <InfiniteList
            items={approvalList}
            loading={approvalHistoryListLoading}
            hasNextPage={
              approvalHistoryListData?.approvalHistorySearch?.pageInfo
                .hasNextPage
            }
            totalRowsCount={
              totalsData?.approvalHistorySearchFilterTotals?.totalCount1
            }
            showFooter={showFooter}
            selectionMode={SelectionMode.none}
            columns={gridColumns.filter((_column) => _column.isVisible)}
            onRenderItemColumn={_renderItemColumn}
            onRenderFooterItemColumn={_renderDetailsFooterItemColumn}
            onColumnHeaderClick={(_, column) => {
              if (column) _onColumnClick(column);
            }}
            onLoadMore={() => handleSearch(true, false, globalState)}
            onRenderRow={_onRenderRow}
          />
        </Stack>
      )}
      {!listMode && selectedItem && (
        <ViewTransactionApprovalDetails
          listMode={listMode}
          selectedItem={selectedItem}
        />
      )}
    </Modal>
  );
};
